import Sidebar from "@/components/Sidebar";
import SubscriptionWidget from "@/components/SubscriptionWidget";
import { Button } from "@/components/ui/button";
import { Check, Zap, Crown, TrendingUp, DollarSign, BookOpen, FileSpreadsheet, Wand2, Lock, Barcode, ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const PLANS = [
  {
    id: "start",
    name: "Start",
    price: 89,
    limit: 100,
    features: [
      "Até 100 produtos/mês",
      "Busca de imagens em catálogos brasileiros",
      "Descrições e SEO gerados por IA",
      "Publicação em lote (até 100 por batch)",
      "Exportação CSV / Excel / ZIP",
      "Suporte por e-mail",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 169,
    limit: 500,
    highlighted: true,
    features: [
      "Até 500 produtos/mês",
      "Tudo do plano Start",
      "Publicação em lote (até 500 por batch)",
      "Filtro Gemini Vision (descarta imagens ruins)",
      "Rollback em massa da Nuvemshop",
      "Precedência de preço da planilha",
      "Suporte prioritário",
    ],
  },
  {
    id: "business",
    name: "Business",
    price: 349,
    limit: 2500,
    features: [
      "Até 2.500 produtos/mês",
      "Tudo do plano Pro",
      "Integração com Bling, Tiny e Omie",
      "Import direto do seu ERP (em breve)",
      "Retomada automática de batch (em breve)",
      "Relatórios mensais por e-mail",
      "Suporte via WhatsApp",
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 699,
    limit: 5000,
    features: [
      "Até 5.000 produtos/mês",
      "Tudo do plano Business",
      "Múltiplas lojas Nuvemshop",
      "Detecção de duplicatas por IA",
      "Custom onboarding + treinamento",
      "SLA de 4h úteis",
      "Gerente de conta dedicado",
    ],
  },
  {
    id: "agencias",
    name: "Agências",
    price: 1299,
    limit: 10000,
    features: [
      "Até 10.000 produtos/mês",
      "Tudo do plano Enterprise",
      "Feito para agências que gerenciam várias lojas",
      "Atualização de preços/estoque em massa",
      "Prioridade máxima de processamento",
      "Gerente de conta dedicado",
    ],
  },
];

function PlanCard({ plan, onSelect, loading }) {
  const highlighted = plan.highlighted;
  return (
    <div
      className={`relative border rounded-md p-6 flex flex-col ${
        highlighted
          ? "border-[#002FA7] bg-white shadow-lg ring-2 ring-[#002FA7]/20"
          : "border-zinc-200 bg-white"
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#002FA7] text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-sm flex items-center gap-1">
          <Crown className="w-3 h-3" /> Mais escolhido
        </div>
      )}
      <div className="mb-4">
        <div className="eyebrow mb-1">{plan.name}</div>
        <div className="font-display text-4xl font-bold text-[#09090B]">
          R$ {plan.price.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          <span className="text-sm font-mono font-normal text-zinc-500 ml-1">/mês</span>
        </div>
        <div className="text-xs font-mono text-zinc-500 mt-2">
          até <span className="text-[#002FA7] font-semibold">{plan.limit.toLocaleString("pt-BR")}</span> produtos/mês
        </div>
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-xs text-zinc-700">
            <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${highlighted ? "text-[#002FA7]" : "text-emerald-600"}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <Button
        onClick={() => onSelect(plan)}
        disabled={loading}
        className={`rounded-md w-full h-11 ${
          highlighted
            ? "brand-primary text-white"
            : "bg-white border border-zinc-300 text-[#09090B] hover:bg-zinc-50"
        }`}
        data-testid={`plan-select-${plan.id}`}
      >
        <Zap className="w-4 h-4 mr-2" />
        Começar 7 dias grátis
      </Button>
      <div className="text-[10px] text-zinc-400 font-mono text-center mt-2">
        cartão obrigatório · sem cobrança durante o trial
      </div>
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = !!user;
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (!isAuthed) return;
    api.get("/user/monthly-usage").then((r) => setUsage(r.data)).catch(() => {});
  }, [isAuthed]);

  const selectPlan = async (plan) => {
    if (!isAuthed) {
      // Send unauthenticated visitors through registration first, remember which plan they picked
      try { sessionStorage.setItem("pendingPlan", plan.id); } catch (_) {}
      navigate(`/register?plan=${plan.id}`);
      return;
    }
    navigate(`/checkout/${plan.id}`);
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {isAuthed ? (
        <Sidebar />
      ) : (
        <header className="fixed top-0 left-0 right-0 z-20 border-b border-zinc-200 bg-white/95 backdrop-blur">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 py-3 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg text-[#09090B]">
              <div className="w-8 h-8 rounded-md brand-primary flex items-center justify-center">
                <Barcode className="w-4 h-4 text-white" />
              </div>
              <span>MAGIC SIP</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/" className="hidden sm:inline text-sm text-zinc-600 hover:text-[#002FA7]">
                <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Voltar à home
              </Link>
              <Link to="/login">
                <Button variant="outline" className="rounded-md border-zinc-300 h-9" data-testid="pricing-header-login">
                  Entrar
                </Button>
              </Link>
              <Link to="/register">
                <Button className="brand-primary text-white rounded-md h-9" data-testid="pricing-header-register">
                  Testar grátis
                </Button>
              </Link>
            </div>
          </div>
        </header>
      )}
      <main className={`flex-1 px-4 sm:px-8 py-8 max-w-7xl mx-auto ${isAuthed ? "" : "pt-24"}`}>
        {isAuthed && <SubscriptionWidget />}

        <header className="mb-8 text-center">
          <div className="eyebrow mb-3">Planos e preços · assinatura mensal</div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold text-[#09090B] mb-3">
            Escolha seu plano Magic SIP
          </h1>
          <p className="text-sm text-zinc-500 max-w-2xl mx-auto">
            <span className="font-semibold text-emerald-700">7 dias grátis para testar</span> com até 10 produtos publicados.
            Cartão obrigatório no cadastro — <span className="font-semibold text-[#09090B]">nada é cobrado durante o trial</span>.
            No 8º dia iniciamos a mensalidade fixa do plano escolhido.
            <span className="block mt-2 text-xs text-zinc-400">
              PIX, boleto e cartão aceitos via Mercado Pago · Cancele quando quiser sem multa · NF-e sob demanda
            </span>
          </p>
        </header>

        {/* Consumption widget (this month) */}
        {usage && (
          <div className="border border-[#002FA7]/20 bg-gradient-to-br from-white to-blue-50/40 rounded-md p-5 mb-8 max-w-4xl mx-auto" data-testid="usage-widget">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="eyebrow mb-1 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Consumo · {usage.month}
                </div>
                <div className="font-display text-3xl font-bold text-[#09090B]" data-testid="usage-products-count">
                  {usage.products_published.toLocaleString("pt-BR")}
                </div>
                <div className="text-xs font-mono text-zinc-500">produtos publicados</div>
              </div>
              <div>
                <div className="eyebrow mb-1 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Custo IA (estimativa)
                </div>
                <div className="font-display text-3xl font-bold text-violet-800" data-testid="usage-llm-cost">
                  US$ {usage.llm_cost_estimate_usd.toFixed(2)}
                </div>
                <div className="text-[10px] font-mono text-zinc-500">
                  ~$0.004/produto processado com IA + Vision
                </div>
              </div>
              <div>
                <div className="eyebrow mb-1">Cobrança neste mês</div>
                <div className="font-display text-3xl font-bold text-[#002FA7]" data-testid="usage-tier-price">
                  R$ {usage.current_tier.price.toFixed(2).replace(".", ",")}
                </div>
                <div className="text-[10px] font-mono text-zinc-500">
                  Faixa <span className="font-semibold text-[#09090B]">{usage.current_tier.name}</span> · até {usage.current_tier.limit.toLocaleString("pt-BR")} produtos
                </div>
              </div>
            </div>
            {usage.over_top_limit && (
              <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                Você ultrapassou o limite da faixa Enterprise ({PLANS[3].limit.toLocaleString("pt-BR")} produtos). Fale conosco para cotar excedente.
              </div>
            )}
          </div>
        )}

        <div id="pricing-tiers" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-12 scroll-mt-6">
          {PLANS.filter((p) => p.id !== "micro").map((p) => (
            <PlanCard key={p.id} plan={p} onSelect={selectPlan} loading={false} />
          ))}
        </div>

        {/* Manual preview — sales pitch. Full manual gated by subscription at /manual */}
        <section
          className="border border-zinc-200 bg-white rounded-md p-6 sm:p-8 mb-12 max-w-4xl mx-auto"
          data-testid="pricing-manual-preview"
        >
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-md brand-primary text-white flex items-center justify-center">
              <BookOpen className="w-4 h-4" />
            </div>
            <div className="eyebrow">Prévia do Manual</div>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#09090B] mb-1">
            Veja como é rápido cadastrar 1.000 produtos
          </h2>
          <p className="text-sm text-zinc-500 mb-6">
            2 das 9 seções do manual de uso — o restante fica disponível ao ativar seu trial gratuito.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Section 1 — Planilha */}
            <div className="border border-zinc-200 rounded-md p-5" data-testid="pricing-manual-section-planilha">
              <div className="flex items-center gap-2 mb-3">
                <FileSpreadsheet className="w-5 h-5 text-[#002FA7]" />
                <div className="font-display font-semibold text-[#09090B]">1 · Prepare a planilha</div>
              </div>
              <p className="text-sm text-zinc-700 leading-relaxed mb-3">
                Suporta CSV e XLSX. Colunas mínimas obrigatórias:
              </p>
              <ul className="text-sm text-zinc-700 space-y-1 mb-3">
                <li>
                  <code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-xs text-[#002FA7]">nome</code>{" "}
                  · nome comercial do produto
                </li>
                <li>
                  <code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-xs text-[#002FA7]">categoria</code>{" "}
                  · segmento (medicamento, alimento, construcao…)
                </li>
                <li>
                  <code className="bg-zinc-100 px-1.5 py-0.5 rounded font-mono text-xs text-[#002FA7]">preco</code>{" "}
                  · valor em BRL (89,90 ou R$ 89,90)
                </li>
              </ul>
              <p className="text-xs text-zinc-500">
                Colunas opcionais como <code className="font-mono">sku</code>,{" "}
                <code className="font-mono">descricao</code>, <code className="font-mono">preco_promocional</code>,{" "}
                <code className="font-mono">estoque</code> pulam a IA e usam o valor da planilha.
              </p>
            </div>

            {/* Section 2 — Execução */}
            <div className="border border-zinc-200 rounded-md p-5" data-testid="pricing-manual-section-execucao">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="w-5 h-5 text-[#002FA7]" />
                <div className="font-display font-semibold text-[#09090B]">2 · Execute o batch</div>
              </div>
              <ol className="text-sm text-zinc-700 space-y-1.5 list-decimal ml-5 mb-3">
                <li>Vá em <strong>Publicar → Lote (até 1000)</strong></li>
                <li>Cole os produtos ou faça upload da planilha</li>
                <li>
                  Clique em <strong>Preparar</strong> — a IA busca imagens em catálogos VTEX
                  (Drogaria SP, Leroy Merlin, Kabum…), filtra imagens irrelevantes com Gemini Vision
                  e gera descrição/SEO
                </li>
                <li>Revise a tabela, ajuste preços e clique em <strong>Publicar selecionados</strong></li>
              </ol>
              <p className="text-xs text-zinc-500">
                Se navegar para outra aba, o batch continua rodando em segundo plano —
                nada é perdido.
              </p>
            </div>
          </div>

          {/* Locked teaser + CTA */}
          <div className="mt-6 border border-dashed border-zinc-300 bg-zinc-50 rounded-md p-5 flex items-start gap-4">
            <Lock className="w-6 h-6 text-zinc-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-[#09090B] mb-1">
                + 7 seções disponíveis para assinantes
              </div>
              <p className="text-xs text-zinc-600 mb-3">
                Catálogos por categoria · Integração com ERPs (Nuvemshop, Bling OAuth, Tiny, Omie) ·
                Planos e cobrança · Segurança & LGPD · Erros comuns · Suporte
              </p>
              <Button
                onClick={() => {
                  const el = document.getElementById("pricing-tiers");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="brand-primary text-white rounded-md h-9 px-4 text-sm"
                data-testid="pricing-manual-cta"
              >
                <Zap className="w-3.5 h-3.5 mr-1.5" /> Começar 7 dias grátis
              </Button>
            </div>
          </div>
        </section>

        <div className="border border-zinc-200 bg-white rounded-md p-6 max-w-3xl mx-auto">
          <div className="eyebrow mb-3">Perguntas frequentes</div>
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-semibold text-zinc-900 mb-1">Como funciona o trial de 7 dias?</div>
              <p className="text-zinc-600 text-xs">
                Ao escolher qualquer plano, você tem 7 dias para testar publicando até 10 produtos.
                O cartão é obrigatório para validar a identidade, mas <strong>nenhum valor é cobrado</strong>
                durante o trial. No 8º dia iniciamos a mensalidade fixa do plano escolhido — a menos que
                você cancele antes disso nas Configurações.
              </p>
            </div>
            <div>
              <div className="font-semibold text-zinc-900 mb-1">Escolhi o plano errado. Posso mudar?</div>
              <p className="text-zinc-600 text-xs">
                Sim. Você pode fazer upgrade/downgrade a qualquer momento nas Configurações.
                A cobrança do novo plano começa no próximo ciclo mensal.
              </p>
            </div>
            <div>
              <div className="font-semibold text-zinc-900 mb-1">O que acontece se eu ultrapassar o limite de produtos do plano?</div>
              <p className="text-zinc-600 text-xs">
                Novas publicações são bloqueadas até o próximo ciclo (renovação mensal) ou até o upgrade.
                Você pode fazer upgrade imediato para desbloquear na hora.
              </p>
            </div>
            <div>
              <div className="font-semibold text-zinc-900 mb-1">Posso cancelar quando quiser?</div>
              <p className="text-zinc-600 text-xs">
                Sim, sem multa. Ao cancelar, os produtos publicados na sua loja Nuvemshop via Magic SIP
                têm a <strong>imagem substituída por um placeholder</strong> ("Produto indisponível — Retorne para Magic SIP")
                e a descrição substituída por texto informativo. <strong>Nome, SKU e preço permanecem visíveis.</strong>
                Ao renovar, tudo é restaurado sem gastar novos créditos de IA.
              </p>
            </div>
            <div>
              <div className="font-semibold text-zinc-900 mb-1">Aceita PIX e boleto?</div>
              <p className="text-zinc-600 text-xs">
                Sim, via Mercado Pago. Cartão de crédito é obrigatório para o trial gratuito;
                PIX e boleto são aceitos para pagamentos mensais recorrentes após o trial.
              </p>
            </div>
            <div>
              <div className="font-semibold text-zinc-900 mb-1">Custo de IA é cobrado à parte?</div>
              <p className="text-zinc-600 text-xs">
                Não. O valor do plano já inclui toda a inteligência artificial (busca de imagens,
                descrições, filtro Vision). O "Custo IA estimado" mostrado é apenas transparência
                sobre o que a Magic SIP paga por trás para operar sua conta.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
